import { useEffect, useRef, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useChatContext } from '@/contexts/ChatContext';
import { useAuth } from '@/contexts/auth-context';

/**
 * State returned by useInitialMessage hook
 */
export interface InitialMessageState {
  /**
   * Whether the initial message should be sent
   */
  shouldSendInitialMessage: boolean;
  
  /**
   * The initial message content (if any)
   */
  initialMessage: string | null;
  
  /**
   * Clear the initial message state
   */
  clearInitialMessage: () => void;
  
  /**
   * Error that occurred while sending initial message
   */
  error: Error | null;
}

/**
 * Hook to handle initial message from query parameters
 * 
 * This hook consolidates the logic for:
 * - Extracting query param from URL
 * - Normalizing URL (removing query param)
 * - Sending initial message once chat is ready
 * - Preventing duplicate sends
 * 
 * @returns InitialMessageState with message state and controls
 */
export function useInitialMessage(): InitialMessageState {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { isInitialized, user } = useAuth();
  const {
    setInputMessage,
    setInitialMessage,
    sendMessage,
    userId,
    isLoading: chatIsLoading,
    initialMessage: contextInitialMessage,
  } = useChatContext();

  const [error, setError] = useState<Error | null>(null);
  const hasSentInitialMessage = useRef(false);
  const queryParam = searchParams?.get("query") ?? null;

  // Effect for hydrating chat input from query params (e.g., marketing handoff)
  useEffect(() => {
    if (!queryParam) return;

    setInputMessage(queryParam);
    setInitialMessage(queryParam);
    hasSentInitialMessage.current = false;
    setError(null);

    // Normalize URL back to /chat so query param does not persist on refresh or chat switches
    router.replace("/chat");
  }, [queryParam, setInputMessage, setInitialMessage, router]);

  // Effect for sending the initial message once the chat controller is ready
  useEffect(() => {
    if (
      isInitialized &&
      user &&
      userId &&
      !chatIsLoading &&
      contextInitialMessage &&
      !hasSentInitialMessage.current
    ) {
      hasSentInitialMessage.current = true; // Prevent re-sending on re-renders

      // eslint-disable-next-line no-console
      console.log("[useInitialMessage] Sending initial message from query:", contextInitialMessage);

      sendMessage(contextInitialMessage)
        .catch((error) => {
          console.error("[useInitialMessage] Failed to send initial query:", error);
          setError(error instanceof Error ? error : new Error(String(error)));
        })
        .finally(() => {
          // Clean the message from context after attempting to send
          setInitialMessage(null);
        });
    }
  }, [
    isInitialized,
    user,
    userId,
    chatIsLoading,
    contextInitialMessage,
    sendMessage,
    setInitialMessage,
  ]);

  const clearInitialMessage = () => {
    setInitialMessage(null);
    setError(null);
    hasSentInitialMessage.current = false;
  };

  return {
    shouldSendInitialMessage: !!contextInitialMessage && !hasSentInitialMessage.current,
    initialMessage: contextInitialMessage,
    clearInitialMessage,
    error,
  };
}

